/**
 * Effect application.
 *
 * Every state change in A LIFE flows through `applyEffect`. Content declares
 * *what* should happen; this module owns *how*. That single funnel is what makes
 * the simulation auditable: the log, the delayed-consequence queue and the
 * end-of-life report are all populated as side effects of one code path.
 *
 * Effects mutate a draft state owned by the engine. The engine clones the state
 * once per step, so callers never observe a half-applied turn.
 */

import { Rng } from './rng';
import type {
  AssetClassId,
  Bond,
  Debt,
  Effect,
  LogCategory,
  LogEntry,
  LogTone,
  Person,
  PersonKind,
  PersonRef,
  PlayerState,
} from './types';
import { clamp, clamp100, netWorth, peopleOfKind, bondScore } from './derived';
import { evaluateAll } from './conditions';
import { ASSET_CLASSES, BUSINESS_ARCHETYPES, CAREER_FIELDS, EDUCATION_PROGRAMS, ASPIRATIONS } from './content';
import { CHILD_NAMES, FIRST_NAMES, SURNAMES } from './content/names';

export interface EffectContext {
  state: PlayerState;
  rng: Rng;
  /** Person the current event is "about", if any. */
  subjectId?: string | null;
  /** Source label used for scheduled effects and log provenance. */
  source?: string;
  sourceEventId?: string | null;
}

/* ------------------------------------------------------------------ *
 * Small utilities
 * ------------------------------------------------------------------ */

function nextId(state: PlayerState, prefix: string): string {
  const n = (state.counters['_seq'] ?? 0) + 1;
  state.counters['_seq'] = n;
  return `${prefix}_${n}`;
}

export function pushLog(
  state: PlayerState,
  entry: {
    category: LogCategory;
    tone: LogTone;
    title: string;
    detail?: string;
    weight?: number;
    tags?: string[];
  },
): LogEntry {
  const full: LogEntry = {
    age: state.age,
    category: entry.category,
    tone: entry.tone,
    title: entry.title,
    weight: entry.weight ?? 1,
    tags: entry.tags ?? [],
  };
  if (entry.detail) full.detail = entry.detail;
  state.log.push(full);
  return full;
}

function applyNumeric(current: number, mode: 'add' | 'mult' | 'set', value: number): number {
  switch (mode) {
    case 'add':
      return current + value;
    case 'mult':
      return current * value;
    case 'set':
      return value;
    default:
      return current;
  }
}

export function resolvePerson(ctx: EffectContext, ref: PersonRef): Person | undefined {
  const { state } = ctx;
  switch (ref.r) {
    case 'id':
      return state.people.find((p) => p.id === ref.id && p.alive);
    case 'spouse':
      return state.people.find((p) => p.kind === 'spouse' && p.alive);
    case 'partner':
      return state.people.find((p) => (p.kind === 'spouse' || p.kind === 'partner') && p.alive);
    case 'subject':
      return ctx.subjectId ? state.people.find((p) => p.id === ctx.subjectId && p.alive) : undefined;
    case 'kind': {
      const pool = peopleOfKind(state, ref.kind);
      if (pool.length === 0) return undefined;
      switch (ref.pick ?? 'first') {
        case 'closest':
          return pool.reduce((a, b) => (bondScore(b) > bondScore(a) ? b : a));
        case 'weakest':
          return pool.reduce((a, b) => (bondScore(b) < bondScore(a) ? b : a));
        case 'random':
          return ctx.rng.pick(pool);
        default:
          return pool[0];
      }
    }
    default:
      return undefined;
  }
}

function adjustBond(person: Person, dim: keyof Bond, delta: number): void {
  person.bond[dim] = clamp100(person.bond[dim] + delta);
}

/* ------------------------------------------------------------------ *
 * Money helpers
 * ------------------------------------------------------------------ */

/**
 * Spend money, drawing from cash first, then liquid holdings, then consumer
 * debt. Returns how much ended up as new debt.
 */
export function spend(ctx: EffectContext, amount: number, label: string): number {
  const f = ctx.state.finance;
  if (amount <= 0) {
    f.cash -= amount; // negative spend = income
    return 0;
  }
  ctx.state.finance.lifetimeSpending += amount;
  let remaining = amount;
  const fromCash = Math.min(f.cash, remaining);
  f.cash -= fromCash;
  remaining -= fromCash;
  if (remaining <= 0) return 0;

  // Liquidate in order of least disruption.
  const order: AssetClassId[] = ['conservative', 'balanced', 'growth', 'speculative'];
  for (const assetId of order) {
    if (remaining <= 0) break;
    const h = f.holdings[assetId];
    if (h.value <= 0) continue;
    const asset = ASSET_CLASSES[assetId];
    // Illiquid assets suffer a haircut when sold under pressure.
    const haircut = 1 - (1 - asset.liquidity) * 0.25;
    const raise = Math.min(h.value, remaining / haircut);
    h.value -= raise;
    h.contributed = Math.max(0, h.contributed - raise);
    remaining -= raise * haircut;
  }
  if (remaining <= 1) return 0;

  addDebt(ctx.state, {
    kind: 'consumer',
    label: `Emergency borrowing (${label})`,
    amount: Math.round(remaining),
    rate: 0.174,
    years: 5,
  });
  return Math.round(remaining);
}

export function addDebt(
  state: PlayerState,
  spec: { kind: Debt['kind']; label: string; amount: number; rate: number; years: number },
): Debt {
  const years = Math.max(1, spec.years);
  const debt: Debt = {
    id: nextId(state, 'debt'),
    kind: spec.kind,
    label: spec.label,
    principal: Math.round(spec.amount),
    rate: spec.rate,
    minPayment: Math.round((spec.amount / years) * (1 + spec.rate)),
    originAge: state.age,
  };
  state.finance.debts.push(debt);
  return debt;
}

export function payDebt(state: PlayerState, amount: number, kind?: Debt['kind']): number {
  let budget = Math.min(amount, state.finance.cash);
  if (budget <= 0) return 0;
  const targets = state.finance.debts
    .filter((d) => !kind || d.kind === kind)
    .sort((a, b) => b.rate - a.rate);
  let paid = 0;
  for (const d of targets) {
    if (budget <= 0) break;
    const pay = Math.min(d.principal, budget);
    d.principal -= pay;
    budget -= pay;
    paid += pay;
  }
  state.finance.cash -= paid;
  state.finance.debts = state.finance.debts.filter((d) => d.principal > 1);
  return Math.round(paid);
}

function buyAsset(ctx: EffectContext, assetId: AssetClassId, amount: number): void {
  const f = ctx.state.finance;
  const spendable = Math.min(amount, f.cash);
  if (spendable <= 0) return;
  f.cash -= spendable;
  const h = f.holdings[assetId];
  h.value += spendable;
  h.contributed += spendable;
  ctx.state.habits.investing = clamp100(ctx.state.habits.investing + 2);
}

function sellAsset(ctx: EffectContext, assetId: AssetClassId, amount: number): void {
  const f = ctx.state.finance;
  const h = f.holdings[assetId];
  const raise = amount <= 0 ? h.value : Math.min(h.value, amount);
  if (raise <= 0) return;
  const asset = ASSET_CLASSES[assetId];
  const haircut = 1 - (1 - asset.liquidity) * 0.15;
  h.value -= raise;
  h.contributed = Math.max(0, h.contributed - raise);
  f.cash += raise * haircut;
}

/* ------------------------------------------------------------------ *
 * People helpers
 * ------------------------------------------------------------------ */

export function makePerson(
  ctx: EffectContext,
  spec: {
    kind: PersonKind;
    tags?: string[];
    nameHint?: string;
    ageOffset?: number;
    bond?: Partial<Bond>;
  },
): Person {
  const { state, rng } = ctx;
  const isChild = spec.kind === 'child';
  const first = spec.nameHint
    ? spec.nameHint
    : isChild
      ? (rng.pick(CHILD_NAMES) ?? 'Ada')
      : (rng.pick(FIRST_NAMES) ?? 'Alex');
  const name = spec.nameHint ?? `${first} ${rng.pick(SURNAMES) ?? 'Lane'}`;
  const baseAge = isChild ? 0 : clamp(state.age + (spec.ageOffset ?? rng.int(-6, 6)), 1, 100);
  const person: Person = {
    id: nextId(state, spec.kind),
    name: isChild ? first : name,
    kind: spec.kind,
    age: baseAge,
    alive: true,
    bond: {
      trust: clamp100(spec.bond?.trust ?? rng.int(38, 62)),
      closeness: clamp100(spec.bond?.closeness ?? rng.int(32, 58)),
      compatibility: clamp100(spec.bond?.compatibility ?? rng.int(30, 78)),
      support: clamp100(spec.bond?.support ?? rng.int(30, 60)),
      conflict: clamp100(spec.bond?.conflict ?? rng.int(2, 18)),
    },
    lastInteractionAge: state.age,
    tags: spec.tags ?? [],
    meta: {},
  };
  if (isChild) {
    person.bond = { trust: 82, closeness: 88, compatibility: 62, support: 12, conflict: 4 };
  }
  if (spec.kind === 'partner' || spec.kind === 'spouse') {
    // Mild assortative matching: partners resemble the player economically
    // without being a copy of them.
    const anchor = Math.max(26000, state.finance.salary * 0.55 + 22000);
    person.meta['income'] = Math.round(anchor * rng.range(0.55, 1.35));
  }
  state.people.push(person);
  return person;
}

/* ------------------------------------------------------------------ *
 * Career helpers
 * ------------------------------------------------------------------ */

export function salaryForRole(state: PlayerState, fieldId: string, roleLevel: number): number {
  const field = CAREER_FIELDS[fieldId];
  if (!field) return 0;
  const role = field.roles[clamp(roleLevel, 0, field.roles.length - 1)];
  if (!role) return 0;
  // Performance and reputation move pay, but only within a believable band —
  // being excellent is worth roughly a third more, not triple.
  const perf = 0.9 + (state.career.performance / 100) * 0.2;
  const rep = 1 + (state.career.reputation / 100) * field.reputationLeverage * 0.5;
  return Math.round(role.baseSalary * perf * rep);
}

/** Upper bound on pay for a given rung, used to stop salary ratcheting. */
export function salaryCeilingForRole(state: PlayerState, fieldId: string, roleLevel: number): number {
  return Math.round(salaryForRole(state, fieldId, roleLevel) * 1.3);
}

export function setCareerField(state: PlayerState, fieldId: string, roleLevel = 0): void {
  const field = CAREER_FIELDS[fieldId];
  if (!field) return;
  const prevField = state.career.fieldId;
  state.career.fieldId = fieldId;
  state.career.roleLevel = clamp(roleLevel, 0, field.roles.length - 1);
  state.career.status = 'employed';
  state.career.tenure = 0;
  state.career.yearsSinceAdvance = 0;
  const role = field.roles[state.career.roleLevel];
  state.career.workload = role?.workload ?? 50;
  state.career.jobSecurity = field.stability;
  state.career.meaningAlignment = clamp100(field.meaning + (state.traits.curiosity - 50) * 0.1);
  state.finance.salary = salaryForRole(state, fieldId, state.career.roleLevel);
  if (prevField && prevField !== fieldId) {
    state.career.pivots += 1;
    // Reputation is field-specific; a pivot costs most but not all of it.
    state.career.reputation = clamp100(state.career.reputation * 0.45);
  }
}

/**
 * Put a newly employed player into a field they can actually be hired for.
 * Without this, "take the job" left `fieldId` null and the player earned
 * nothing for the rest of their life.
 */
export function autoAssignField(state: PlayerState): void {
  if (state.career.fieldId) {
    setCareerField(state, state.career.fieldId, state.career.roleLevel);
    return;
  }
  const rank: Record<string, number> = { highschool: 0, vocational: 1, bachelor: 2, master: 3, doctorate: 4 };
  const playerRank = rank[state.education.level] ?? 0;

  // Weighted by how well the player actually fits the field, not by which pays
  // most. Choosing max salary made every simulated life a software engineer,
  // which collapsed the whole point of having twelve career worlds.
  const candidates: { id: string; weight: number }[] = [];
  for (const field of Object.values(CAREER_FIELDS)) {
    if (field.entry.education && (rank[field.entry.education] ?? 0) > playerRank) continue;
    if (field.entry.credential && !state.education.credentials.includes(field.entry.credential)) continue;
    const meets = Object.entries(field.entry.skills ?? {}).every(
      ([skill, value]) => state.skills[skill as keyof typeof state.skills] >= (value ?? 0),
    );
    if (!meets) continue;

    // Fit: how strong the player already is in the skills this field rewards.
    let fit = 0;
    let total = 0;
    for (const [skill, growth] of Object.entries(field.skillGrowth)) {
      const w = growth ?? 0;
      fit += state.skills[skill as keyof typeof state.skills] * w;
      total += w;
    }
    const fitScore = total > 0 ? fit / total : 20;
    const ambition = (state.traits.ambition - 50) / 50;
    const payPull = (field.roles[0]?.baseSalary ?? 40000) / 60000;
    const meaningPull = field.meaning / 70;
    const weight = Math.max(0.3, fitScore * 0.6 + payPull * 14 * Math.max(0, ambition) + meaningPull * 6);
    candidates.push({ id: field.id, weight });
  }

  if (candidates.length === 0) {
    setCareerField(state, 'hospitality', 0);
    return;
  }
  const seedRng = new Rng(`${state.rng.seed}:field:${state.age}`);
  const idx = seedRng.weightedIndex(candidates.map((c) => c.weight));
  setCareerField(state, candidates[idx < 0 ? 0 : idx]?.id ?? 'hospitality', 0);
}

function promote(state: PlayerState, levels: number): boolean {
  const field = state.career.fieldId ? CAREER_FIELDS[state.career.fieldId] : undefined;
  if (!field) return false;
  const target = clamp(state.career.roleLevel + levels, 0, field.roles.length - 1);
  if (target === state.career.roleLevel) return false;
  state.career.roleLevel = target;
  state.career.promotions += levels > 0 ? 1 : 0;
  state.career.tenure = 0;
  state.career.yearsSinceAdvance = 0;
  const role = field.roles[target];
  if (role) state.career.workload = clamp100((state.career.workload + role.workload) / 2 + 4);
  state.finance.salary = salaryForRole(state, field.id, target);
  return true;
}

function layoff(ctx: EffectContext): void {
  const { state } = ctx;
  state.career.layoffs += 1;
  state.career.status = 'unemployed';
  state.career.workload = 8;
  state.finance.salary = 0;
  state.career.tenure = 0;
  state.stats.stress = clamp100(state.stats.stress + 14);
  state.stats.happiness = clamp100(state.stats.happiness - 9);
}

/* ------------------------------------------------------------------ *
 * The dispatcher
 * ------------------------------------------------------------------ */

export function applyEffects(ctx: EffectContext, effects: Effect[] | undefined): void {
  if (!effects) return;
  for (const e of effects) applyEffect(ctx, e);
}

export function applyEffect(ctx: EffectContext, effect: Effect): void {
  const { state, rng } = ctx;
  switch (effect.k) {
    /* --- stats ------------------------------------------------- */
    case 'stat':
      state.stats[effect.stat] = clamp100(state.stats[effect.stat] + effect.delta);
      break;
    case 'statSet':
      state.stats[effect.stat] = clamp100(effect.value);
      break;

    /* --- money ------------------------------------------------- */
    case 'cash': {
      if (effect.amount >= 0) {
        state.finance.cash += effect.amount;
        state.finance.lifetimeEarnings += effect.amount;
      } else {
        spend(ctx, -effect.amount, effect.note ?? 'expense');
      }
      break;
    }
    case 'cashIncomeMonths': {
      const monthly = Math.max(1200, (state.finance.salary + state.finance.otherIncome) / 12);
      const amount = monthly * effect.months;
      if (amount >= 0) {
        state.finance.cash += amount;
        state.finance.lifetimeEarnings += amount;
      } else {
        spend(ctx, -amount, effect.note ?? 'expense');
      }
      break;
    }
    case 'salary':
      state.finance.salary = Math.max(0, Math.round(applyNumeric(state.finance.salary, effect.mode, effect.value)));
      break;
    case 'otherIncome':
      state.finance.otherIncome = Math.max(0, Math.round(applyNumeric(state.finance.otherIncome, effect.mode, effect.value)));
      break;
    case 'fixedExpenses':
      state.finance.fixedExpenses = Math.max(0, Math.round(applyNumeric(state.finance.fixedExpenses, effect.mode, effect.value)));
      break;
    case 'lifestyle':
      state.finance.lifestyleLevel = clamp(state.finance.lifestyleLevel + effect.delta, 0, 10);
      break;

    case 'debt': {
      if (effect.op === 'add') {
        addDebt(state, effect);
      } else if (effect.op === 'pay') {
        payDebt(state, effect.amount, effect.kind);
      } else {
        const frac = effect.fraction ?? 1;
        for (const d of state.finance.debts) {
          if (effect.kind && d.kind !== effect.kind) continue;
          d.principal = Math.round(d.principal * (1 - frac));
        }
        state.finance.debts = state.finance.debts.filter((d) => d.principal > 1);
      }
      break;
    }

    case 'invest': {
      if (effect.op === 'shock') {
        const h = state.finance.holdings[effect.assetId];
        h.value = Math.max(0, h.value * (1 + effect.fraction));
      } else if (effect.op === 'buy') {
        buyAsset(ctx, effect.assetId, effect.amount);
      } else {
        sellAsset(ctx, effect.assetId, effect.amount);
      }
      break;
    }

    case 'property': {
      const f = state.finance;
      if (effect.op === 'buy') {
        if (f.property.owned) break;
        const deposit = Math.min(effect.deposit, f.cash);
        f.cash -= deposit;
        const mortgage = Math.max(0, effect.value - deposit);
        const debt = addDebt(state, {
          kind: 'mortgage',
          label: 'Mortgage',
          amount: mortgage,
          rate: effect.rate,
          years: effect.years,
        });
        f.property = { owned: true, value: effect.value, mortgageDebtId: debt.id, purchaseAge: state.age };
        f.holdings['property'].value = effect.value;
        f.holdings['property'].contributed = deposit;
        state.flags['ownsHome'] = true;
      } else {
        if (!f.property.owned) break;
        const proceeds = f.property.value;
        const mortgage = f.debts.find((d) => d.id === f.property.mortgageDebtId);
        const outstanding = mortgage?.principal ?? 0;
        f.debts = f.debts.filter((d) => d.id !== f.property.mortgageDebtId);
        f.cash += Math.max(0, proceeds - outstanding);
        f.property = { owned: false, value: 0, mortgageDebtId: null, purchaseAge: 0 };
        f.holdings['property'].value = 0;
        f.holdings['property'].contributed = 0;
        state.flags['ownsHome'] = false;
      }
      break;
    }
    case 'retirement':
      state.finance.retirementAssets = Math.max(
        0,
        Math.round(applyNumeric(state.finance.retirementAssets, effect.mode, effect.value)),
      );
      break;

    /* --- person-level attributes -------------------------------- */
    case 'skill':
      state.skills[effect.skill] = clamp100(state.skills[effect.skill] + effect.delta);
      break;
    case 'trait':
      state.traits[effect.trait] = clamp100(state.traits[effect.trait] + effect.delta);
      break;
    case 'habit':
      state.habits[effect.habit] = clamp100(state.habits[effect.habit] + effect.delta);
      break;

    /* --- career ------------------------------------------------- */
    case 'career': {
      switch (effect.op) {
        case 'setStatus': {
          const previous = state.career.status;
          state.career.status = effect.status;
          if (previous === 'student' && effect.status !== 'student') {
            // Part-time student income stops when study does.
            state.finance.otherIncome = 0;
          }
          if (effect.status === 'unemployed' || effect.status === 'break' || effect.status === 'retired') {
            state.finance.salary = 0;
            state.career.workload = effect.status === 'break' ? 5 : 8;
          }
          if (effect.status === 'break') state.career.breaksTaken += 1;
          if (effect.status === 'employed') {
            autoAssignField(state);
            state.career.status = 'employed';
            state.flags['everEmployed'] = true;
          }
          break;
        }
        case 'setField':
          setCareerField(state, effect.fieldId, effect.roleLevel ?? 0);
          break;
        case 'promote':
          promote(state, effect.levels ?? 1);
          break;
        case 'demote':
          promote(state, -(effect.levels ?? 1));
          break;
        case 'layoff':
          layoff(ctx);
          break;
        case 'quit':
          state.career.status = 'unemployed';
          state.finance.salary = 0;
          state.career.workload = 8;
          state.career.tenure = 0;
          break;
        case 'retire':
          state.career.status = 'retired';
          state.finance.salary = 0;
          state.career.workload = 0;
          state.flags['retired'] = true;
          break;
        case 'adjust':
          state.career[effect.field] = clamp100(state.career[effect.field] + effect.delta);
          if (effect.field === 'performance' || effect.field === 'reputation') {
            if (state.career.fieldId && state.career.status === 'employed') {
              state.finance.salary = salaryForRole(state, state.career.fieldId, state.career.roleLevel);
            }
          }
          break;
        default:
          break;
      }
      break;
    }

    /* --- education ---------------------------------------------- */
    case 'education': {
      if (effect.op === 'enroll') {
        const program = EDUCATION_PROGRAMS[effect.programId];
        if (program) {
          state.education.enrollment = {
            programId: program.id,
            yearsRemaining: program.years,
            annualCost: program.annualCost,
            workCapacity: program.workCapacity,
          };
          if (program.loanFraction && program.loanFraction > 0) {
            const total = program.annualCost * program.years * program.loanFraction;
            if (total > 500) {
              addDebt(state, {
                kind: 'student',
                label: `${program.label} loan`,
                amount: total,
                rate: 0.052,
                years: 15,
              });
              state.finance.cash += total;
            }
          }
        }
      } else if (effect.op === 'dropOut') {
        state.education.enrollment = null;
      } else {
        if (!state.education.credentials.includes(effect.id)) state.education.credentials.push(effect.id);
        if (effect.level) {
          const rank: Record<string, number> = { highschool: 0, vocational: 1, bachelor: 2, master: 3, doctorate: 4 };
          if ((rank[effect.level] ?? 0) > (rank[state.education.level] ?? 0)) {
            state.education.level = effect.level;
          }
        }
      }
      break;
    }

    /* --- relationships ------------------------------------------ */
    case 'relationship': {
      const person = resolvePerson(ctx, effect.who);
      if (person) {
        adjustBond(person, effect.dim, effect.delta);
        if (effect.delta > 0 && effect.dim !== 'conflict') person.lastInteractionAge = state.age;
      }
      break;
    }
    case 'relationshipAll': {
      for (const p of state.people) {
        if (!p.alive) continue;
        if (effect.kind && p.kind !== effect.kind) continue;
        adjustBond(p, effect.dim, effect.delta);
      }
      break;
    }
    case 'person': {
      if (effect.op === 'add') {
        const p = makePerson(ctx, effect);
        ctx.subjectId = p.id;
      } else if (effect.op === 'remove') {
        const p = resolvePerson(ctx, effect.who);
        if (p) {
          p.alive = false;
          p.tags.push(effect.reason ?? 'left');
        }
      } else if (effect.op === 'convert') {
        const p = resolvePerson(ctx, effect.who);
        if (p) p.kind = effect.to;
      } else {
        const p = resolvePerson(ctx, effect.who);
        if (p) {
          p.lastInteractionAge = state.age;
          adjustBond(p, 'closeness', 3);
        }
      }
      break;
    }
    case 'marital': {
      const prev = state.family.maritalStatus;
      state.family.maritalStatus = effect.status;
      if (effect.status === 'married' && prev !== 'married') {
        state.family.marriages += 1;
        state.family.unionSinceAge = state.age;
        state.flags['everMarried'] = true;
      }
      if (effect.status === 'divorced' && prev !== 'divorced') {
        state.family.divorces += 1;
        state.family.unionSinceAge = null;
      }
      if (effect.status === 'partnered' && !state.family.unionSinceAge) {
        state.family.unionSinceAge = state.age;
      }
      if (effect.status === 'single' || effect.status === 'divorced' || effect.status === 'widowed') {
        state.family.unionSinceAge = null;
      }
      break;
    }
    case 'family': {
      if (effect.op === 'childBorn') {
        const child = makePerson(ctx, { kind: 'child', ageOffset: -state.age });
        child.age = 0;
        state.family.childrenBorn += 1;
        state.family.dependents += 1;
        state.flags['hasChildren'] = true;
        pushLog(state, {
          category: 'family',
          tone: 'milestone',
          title: `${child.name} was born`,
          weight: 9,
          tags: ['child', 'milestone'],
        });
      } else {
        state.family.householdHarmony = clamp100(state.family.householdHarmony + effect.delta);
      }
      break;
    }

    /* --- business ------------------------------------------------ */
    case 'business': {
      const b = state.business;
      switch (effect.op) {
        case 'start': {
          const arch = BUSINESS_ARCHETYPES[effect.archetypeId];
          if (!arch) break;
          const capital = Math.min(effect.capital, state.finance.cash + 1);
          state.finance.cash -= capital;
          b.active = true;
          b.archetypeId = arch.id;
          b.name = arch.label;
          b.capital = capital;
          b.revenue = Math.round(capital * arch.initialRevenueMultiple * rng.range(0.5, 1.2));
          b.operatingCost = Math.round(b.revenue * arch.costRatio);
          b.growth = arch.growthMean;
          b.risk = arch.risk;
          b.health = 62;
          b.timeDemand = arch.timeDemand;
          b.valuation = Math.round(Math.max(0, b.revenue - b.operatingCost) * arch.exitMultiple);
          b.yearsRunning = 0;
          b.exitProceeds = null;
          state.career.status = 'founder';
          state.flags['founder'] = true;
          break;
        }
        case 'invest': {
          const amount = Math.min(effect.amount, state.finance.cash);
          if (amount <= 0) break;
          state.finance.cash -= amount;
          b.capital += amount;
          b.health = clamp100(b.health + amount / Math.max(1500, b.revenue / 8));
          b.growth += 0.02;
          break;
        }
        case 'adjust':
          if (effect.field === 'growth') b.growth += effect.delta;
          else b[effect.field] = clamp100(b[effect.field] + effect.delta);
          break;
        case 'fail': {
          b.failures += 1;
          const debtLeft = Math.round(Math.max(0, b.operatingCost * 0.4 - b.capital));
          b.active = false;
          b.archetypeId = null;
          b.revenue = 0;
          b.operatingCost = 0;
          b.valuation = 0;
          b.capital = 0;
          b.timeDemand = 0;
          b.health = 0;
          if (debtLeft > 500) {
            addDebt(state, { kind: 'business', label: 'Wind-down costs', amount: debtLeft, rate: 0.09, years: 6 });
          }
          if (state.career.status === 'founder') state.career.status = 'unemployed';
          state.flags['businessFailed'] = true;
          break;
        }
        case 'exit': {
          const proceeds = Math.round(b.valuation * (effect.multiple ?? 1));
          state.finance.cash += proceeds;
          state.finance.lifetimeEarnings += proceeds;
          b.exitProceeds = proceeds;
          b.exits += 1;
          b.active = false;
          b.archetypeId = null;
          b.revenue = 0;
          b.operatingCost = 0;
          b.valuation = 0;
          b.timeDemand = 0;
          if (state.career.status === 'founder') state.career.status = 'break';
          state.flags['businessExited'] = true;
          break;
        }
        default:
          break;
      }
      break;
    }

    /* --- burnout -------------------------------------------------- */
    case 'burnout':
      if (effect.op === 'load') state.burnout.load = clamp100(state.burnout.load + effect.delta);
      else {
        state.burnout.stage = effect.stage;
        state.burnout.yearsInStage = 0;
        if (effect.stage === 'burntOut') {
          state.burnout.episodes += 1;
          state.burnout.lastEpisodeAge = state.age;
        }
      }
      break;

    /* --- bookkeeping ---------------------------------------------- */
    case 'flag':
      state.flags[effect.flag] = effect.value;
      break;
    case 'counter':
      state.counters[effect.name] = (state.counters[effect.name] ?? 0) + effect.delta;
      break;

    case 'aspiration': {
      if (effect.op === 'adopt') {
        if (!state.aspirations.some((a) => a.defId === effect.defId)) {
          const def = ASPIRATIONS[effect.defId];
          if (def) {
            state.aspirations.push({
              id: nextId(state, 'asp'),
              defId: def.id,
              kind: def.kind,
              status: 'active',
              progress: 0,
              adoptedAge: state.age,
              resolvedAge: null,
              targetAge: def.regretAge ?? null,
            });
          }
        }
      } else {
        const asp = state.aspirations.find((a) => a.defId === effect.defId);
        if (!asp) break;
        if (effect.op === 'progress') asp.progress = clamp100(asp.progress + effect.delta);
        else {
          asp.status = effect.status;
          asp.resolvedAge = state.age;
          if (effect.status === 'achieved') asp.progress = 100;
        }
      }
      break;
    }

    case 'intention': {
      if (effect.op === 'add') {
        if (!state.inertia.intentions.some((i) => i.id === effect.id)) {
          state.inertia.intentions.push({
            id: effect.id,
            label: effect.label,
            topic: effect.topic,
            createdAge: state.age,
            deferrals: 0,
            urgency: effect.urgency ?? 25,
            decayEventId: effect.decayEventId ?? null,
            resumeEventId: effect.resumeEventId ?? null,
          });
          state.inertia.deferredCount += 1;
        }
      } else if (effect.op === 'resolve') {
        const before = state.inertia.intentions.length;
        state.inertia.intentions = state.inertia.intentions.filter((i) => i.id !== effect.id);
        if (state.inertia.intentions.length < before) state.inertia.actedCount += 1;
      } else {
        const i = state.inertia.intentions.find((x) => x.id === effect.id);
        if (i) {
          i.deferrals += 1;
          i.urgency = clamp100(i.urgency + 8);
          state.inertia.deferredCount += 1;
        }
      }
      break;
    }

    case 'inertia':
      state.inertia.pressure = clamp100(state.inertia.pressure + effect.delta);
      break;

    case 'regret': {
      if (effect.op === 'add') {
        const existing = state.regrets.find((r) => r.id === effect.id);
        if (existing) existing.weight = clamp100(existing.weight + effect.weight * 0.5);
        else {
          state.regrets.push({ id: effect.id, age: state.age, label: effect.label, weight: effect.weight, resolved: false });
        }
      } else {
        const r = state.regrets.find((x) => x.id === effect.id);
        if (r) {
          r.resolved = true;
          r.weight = Math.round(r.weight * 0.3);
        }
      }
      break;
    }

    case 'achievement': {
      if (!state.achievements.some((a) => a.id === effect.id)) {
        state.achievements.push({ id: effect.id, age: state.age, label: effect.label, category: effect.category });
        pushLog(state, {
          category: effect.category,
          tone: 'milestone',
          title: effect.label,
          weight: 7,
          tags: ['achievement'],
        });
      }
      break;
    }

    case 'memory':
      pushLog(state, {
        category: effect.category,
        tone: effect.tone,
        title: effect.title,
        ...(effect.detail ? { detail: effect.detail } : {}),
        weight: effect.weight ?? 3,
      });
      break;

    /* --- time-shifted -------------------------------------------- */
    case 'schedule': {
      state.scheduled.push({
        id: nextId(state, 'sched'),
        dueAge: state.age + Math.max(1, Math.round(effect.afterYears)),
        sourceLabel: effect.label,
        sourceEventId: ctx.sourceEventId ?? null,
        sourceAge: state.age,
        effects: effect.effects,
        eventId: effect.eventId ?? null,
        ...(effect.requires ? { requires: effect.requires } : {}),
        ...(effect.narrative ? { narrative: effect.narrative } : {}),
      });
      break;
    }
    case 'triggerEvent': {
      const after = effect.afterYears ?? 0;
      if (after <= 0) {
        if (!state.pendingEvents.includes(effect.eventId)) state.pendingEvents.push(effect.eventId);
      } else {
        state.scheduled.push({
          id: nextId(state, 'sched'),
          dueAge: state.age + Math.round(after),
          sourceLabel: 'Follow-up',
          sourceEventId: ctx.sourceEventId ?? null,
          sourceAge: state.age,
          effects: [],
          eventId: effect.eventId,
        });
      }
      break;
    }

    /* --- health --------------------------------------------------- */
    case 'health': {
      if (effect.op === 'condition') {
        if (!state.conditions.some((c) => c.id === effect.id)) {
          state.conditions.push({
            id: effect.id,
            label: effect.label,
            severity: clamp(effect.severity, 1, 100),
            onsetAge: state.age,
            chronic: effect.severity >= 40,
          });
        }
      } else {
        state.conditions = state.conditions.filter((c) => c.id !== effect.id);
      }
      break;
    }

    case 'end':
      state.alive = false;
      state.causeOfDeath = effect.cause;
      break;

    default: {
      // Exhaustiveness guard: a new Effect variant must be handled here.
      const never: never = effect;
      void never;
      break;
    }
  }

  // Keep peak net worth fresh for the ending report without a separate pass.
  const nw = netWorth(state);
  if (nw > state.finance.peakNetWorth) state.finance.peakNetWorth = nw;
}

/** Apply effects only if their guard conditions still hold. */
export function applyGuarded(ctx: EffectContext, effects: Effect[], requires?: Effect extends never ? never : Parameters<typeof evaluateAll>[1]): boolean {
  if (!evaluateAll(ctx.state, requires)) return false;
  applyEffects(ctx, effects);
  return true;
}
